import { z } from 'zod';

import { actorSchema, reasonSchema } from './clinical-common.ts';

// Lab analyzers (docs/plans/phase2-lab-connector.md; ADR 0026). Two audiences:
//
// 1. The on-premises lab connector (apps/lab-connector), which authenticates with its
//    device credential and posts what an analyzer sent, parsed but not interpreted. It
//    never sends patient names or demographics: an analyzer's patient fields are dropped
//    by the parsers. Values travel as the text the analyzer sent; the server parses them
//    against the analyte and judges them against the configured ranges.
// 2. The lab, who registers devices, maps analyzer test codes to analytes, sees each
//    device's state, and works the review queue of results that could not be applied.
//
// Sample numbers, values and units travel only in bodies, never in URLs or query strings.

// --- Device configuration --------------------------------------------------------------

export const ANALYZER_PROTOCOLS = ['astm_e1394', 'hl7_v2'] as const;
export type AnalyzerProtocol = (typeof ANALYZER_PROTOCOLS)[number];

export const ANALYZER_PROTOCOL_LABELS: Record<AnalyzerProtocol, string> = {
  astm_e1394: 'ASTM E1381/E1394 (LIS2-A2)',
  hl7_v2: 'HL7 v2 (ORU^R01 over MLLP)',
};

const componentIndexSchema = z.number().int().min(1).max(10);

/**
 * Where the parsers find the sample number and the test code in a vendor's messages.
 * Vendors differ, so this is configuration. The defaults follow the published record
 * layouts; TODO(verify-spec): confirm against each analyzer's interface manual.
 */
export const astmDriverConfigSchema = z.object({
  protocol: z.literal('astm_e1394'),
  /** O record field 3 (Specimen ID) or field 4 (Instrument Specimen ID). */
  specimenIdField: z.enum(['O.3', 'O.4']).default('O.3'),
  specimenIdComponent: componentIndexSchema.default(1),
  /** The component of the Universal Test ID (R record field 3) that carries the code. */
  testCodeComponent: componentIndexSchema.default(4),
  /** The analyzer clock's offset from UTC, for its date-times (they carry no zone). */
  utcOffset: z
    .string()
    .regex(/^[+-](0\d|1[0-4]):[0-5]\d$/)
    .default('+03:00'),
});
export type AstmDriverConfig = z.infer<typeof astmDriverConfigSchema>;

export const hl7DriverConfigSchema = z.object({
  protocol: z.literal('hl7_v2'),
  /** OBR-2 (placer order number) or OBR-3 (filler order number), or SPM-2. */
  specimenIdField: z.enum(['OBR-2', 'OBR-3', 'SPM-2']).default('OBR-3'),
  specimenIdComponent: componentIndexSchema.default(1),
  /** The component of OBX-3 (observation identifier) that carries the code. */
  testCodeComponent: componentIndexSchema.default(1),
  /** Used when OBX-14 or OBR-7 carry no zone. */
  utcOffset: z
    .string()
    .regex(/^[+-](0\d|1[0-4]):[0-5]\d$/)
    .default('+03:00'),
});
export type Hl7DriverConfig = z.infer<typeof hl7DriverConfigSchema>;

export const analyzerDriverConfigSchema = z.discriminatedUnion('protocol', [
  astmDriverConfigSchema,
  hl7DriverConfigSchema,
]);
export type AnalyzerDriverConfig = z.infer<typeof analyzerDriverConfigSchema>;

/** Result statuses the server applies without review (ASTM R.9, HL7 OBX-11). */
export const DEFAULT_ACCEPTED_RESULT_STATUSES = ['F', 'C'] as const;

// --- The connector's calls ---------------------------------------------------------------

/** One result as the analyzer sent it. Nothing here is interpreted by the connector. */
export const analyzerResultInputSchema = z.object({
  /** The sample number read from the barcode, as sent. */
  sampleNumber: z.string().trim().min(1).max(64),
  analyzerTestCode: z.string().trim().min(1).max(64),
  /** The value as sent, e.g. "5.4", "<0.5". */
  value: z.string().max(200),
  unit: z.string().max(40).nullable(),
  /** The analyzer's own abnormal flags (ASTM R.7, HL7 OBX-8). Shown, never used to judge. */
  analyzerFlags: z.string().max(40).nullable(),
  /** ASTM R.9 or HL7 OBX-11, as sent. */
  resultStatus: z.string().max(10).nullable(),
  /** When the analyzer says it measured, if it said. */
  observedAt: z.iso.datetime({ offset: true }).nullable(),
});
export type AnalyzerResultInput = z.infer<typeof analyzerResultInputSchema>;

export const MAX_ANALYZER_RESULTS_PER_UPLOAD = 500;

export const analyzerUploadSchema = z.object({
  /** The connector's key for the analyzer message (a hash), for correlation only. */
  messageKey: z.string().regex(/^[0-9a-f]{16,64}$/),
  protocol: z.enum(ANALYZER_PROTOCOLS),
  /** When the connector received the message from the analyzer. */
  receivedAt: z.iso.datetime({ offset: true }),
  results: z.array(analyzerResultInputSchema).min(1).max(MAX_ANALYZER_RESULTS_PER_UPLOAD),
});
export type AnalyzerUpload = z.infer<typeof analyzerUploadSchema>;

export const analyzerUploadResultSchema = z.object({
  /** New results applied to a sample as unvalidated results. */
  applied: z.number().int(),
  /** New results waiting in the review queue. */
  queued: z.number().int(),
  /** Results the server already had: sending again changes nothing. */
  duplicates: z.number().int(),
});
export type AnalyzerUploadResult = z.infer<typeof analyzerUploadResultSchema>;

/** Problems the connector reports by code; never message content. */
export const CONNECTOR_ERROR_CODES = [
  'checksum_failed',
  'frame_out_of_sequence',
  'frame_invalid',
  'frame_timeout',
  'retries_exhausted',
  'message_too_large',
  'parse_failed',
  'no_results',
  'link_failed',
  'delivery_failed',
  'delivery_refused',
  'spool_expired',
  'spool_failed',
] as const;
export type ConnectorErrorCode = (typeof CONNECTOR_ERROR_CODES)[number];

export const CONNECTOR_ERROR_TEXT: Record<ConnectorErrorCode, string> = {
  checksum_failed: 'A frame from the analyzer failed its checksum and was asked for again.',
  frame_out_of_sequence: 'A frame from the analyzer arrived out of order and was refused.',
  frame_invalid: 'The analyzer sent a frame that is not in the expected format.',
  frame_timeout: 'The analyzer stopped sending in the middle of a message.',
  retries_exhausted: 'The analyzer gave up after repeated failures. Check the cable or network.',
  message_too_large: 'The analyzer sent a message larger than the connector accepts.',
  parse_failed:
    'A message from the analyzer could not be read. Check the protocol settings for this analyzer.',
  no_results: 'A message from the analyzer carried no results.',
  link_failed: 'The connector could not reach the analyzer. Check the cable or network.',
  delivery_failed: 'The connector could not reach Wodi. Results wait on the connector.',
  delivery_refused: 'Wodi refused results from the connector. Check its credential.',
  spool_expired:
    'Results waited too long on the connector and were removed. Run them again on the analyzer.',
  spool_failed:
    'The connector could not save results to its disk. Check the disk space on the connector computer.',
};

export const CONNECTOR_LINK_STATES = ['connected', 'disconnected'] as const;

export const connectorHeartbeatSchema = z.object({
  connectorVersion: z.string().trim().min(1).max(40),
  /** Whether the connector has a live connection to the analyzer. */
  link: z.enum(CONNECTOR_LINK_STATES),
  /** Messages waiting on the connector to be delivered. */
  spooled: z.number().int().min(0).max(1_000_000),
  /** Problems since the last heartbeat, by code. */
  errors: z
    .array(
      z.object({
        code: z.enum(CONNECTOR_ERROR_CODES),
        count: z.number().int().min(1).max(1_000_000),
        lastAt: z.iso.datetime({ offset: true }),
      }),
    )
    .max(CONNECTOR_ERROR_CODES.length),
});
export type ConnectorHeartbeat = z.infer<typeof connectorHeartbeatSchema>;

/** What the connector needs to know, returned on each heartbeat. No patient data. */
export const connectorConfigSchema = z.object({
  analyzerId: z.uuid(),
  name: z.string(),
  driver: analyzerDriverConfigSchema,
});
export type ConnectorConfig = z.infer<typeof connectorConfigSchema>;

// --- Settings > Lab analyzers ---------------------------------------------------------

export const ANALYZER_CONNECTION_STATES = [
  'connected',
  'analyzer_disconnected',
  'connector_offline',
  'never_seen',
] as const;
export type AnalyzerConnectionState = (typeof ANALYZER_CONNECTION_STATES)[number];

export const ANALYZER_CONNECTION_LABELS: Record<AnalyzerConnectionState, string> = {
  connected: 'Connected',
  analyzer_disconnected: 'Analyzer not connected',
  connector_offline: 'Connector offline',
  never_seen: 'Never connected',
};

export const analyzerTestCodeSchema = z.object({
  analyzerCode: z.string(),
  analyteId: z.uuid(),
  analyteName: z.string(),
  /** The unit the analyzer sends; null to compare with the analyte's own unit. */
  expectedUnit: z.string().nullable(),
  analyteUnit: z.string().nullable(),
});
export type AnalyzerTestCodeView = z.infer<typeof analyzerTestCodeSchema>;

export const analyzerSchema = z.object({
  id: z.uuid(),
  facilityId: z.uuid(),
  name: z.string(),
  protocol: z.enum(ANALYZER_PROTOCOLS),
  driver: analyzerDriverConfigSchema,
  acceptedResultStatuses: z.array(z.string()),
  status: z.enum(['active', 'retired']),
  credentialIssuedAt: z.iso.datetime(),
  connection: z.object({
    state: z.enum(ANALYZER_CONNECTION_STATES),
    lastSeenAt: z.iso.datetime().nullable(),
    connectorVersion: z.string().nullable(),
    spooled: z.number().int().nullable(),
  }),
  lastMessageAt: z.iso.datetime().nullable(),
  lastError: z.object({ code: z.string(), text: z.string(), at: z.iso.datetime() }).nullable(),
  testCodes: z.array(analyzerTestCodeSchema),
  /** Results from this analyzer waiting in the review queue. */
  queued: z.number().int(),
  version: z.number().int(),
});
export type AnalyzerView = z.infer<typeof analyzerSchema>;

const analyzerNameSchema = z.string().trim().min(1).max(80);
const resultStatusesSchema = z.array(z.string().trim().min(1).max(10)).min(1).max(10);

export const createAnalyzerBodySchema = z.object({
  name: analyzerNameSchema,
  driver: analyzerDriverConfigSchema,
  acceptedResultStatuses: resultStatusesSchema.default([...DEFAULT_ACCEPTED_RESULT_STATUSES]),
});

export const updateAnalyzerBodySchema = z.object({
  expectedVersion: z.number().int().min(1),
  name: analyzerNameSchema.optional(),
  driver: analyzerDriverConfigSchema.optional(),
  acceptedResultStatuses: resultStatusesSchema.optional(),
});

export const retireAnalyzerBodySchema = z.object({ reason: reasonSchema });

/** The credential is shown once, when it is created or rotated, and never again. */
export const analyzerCredentialSchema = z.object({
  analyzer: analyzerSchema,
  credential: z.string(),
});

export const putAnalyzerTestCodesBodySchema = z.object({
  expectedVersion: z.number().int().min(1),
  testCodes: z
    .array(
      z.object({
        analyzerCode: z.string().trim().min(1).max(64),
        analyteId: z.uuid(),
        expectedUnit: z.string().trim().max(40).nullable(),
      }),
    )
    .max(300),
});

export const analyzerMessageSchema = z.object({
  id: z.uuid(),
  at: z.iso.datetime(),
  kind: z.enum(['results', 'connector_error', 'connection']),
  outcome: z.enum(['succeeded', 'failed']),
  errorCode: z.string().nullable(),
  text: z.string(),
});
export type AnalyzerMessageView = z.infer<typeof analyzerMessageSchema>;

// --- The review queue -----------------------------------------------------------------

export const ANALYZER_QUEUE_REASONS = [
  'unknown_test_code',
  'sample_not_found',
  'sample_not_usable',
  'test_not_ordered',
  'report_not_open',
  'differs_from_entered',
  'unit_mismatch',
  'value_invalid',
  'value_not_possible',
  'status_not_final',
  'ambiguous_request',
  'ranges_conflict',
  // Lab QC (docs/plans/phase3-lab-qc-and-microbiology.md §1.1.6).
  'qc_not_set_up',
] as const;
export type AnalyzerQueueReason = (typeof ANALYZER_QUEUE_REASONS)[number];

export const ANALYZER_QUEUE_REASON_TEXT: Record<AnalyzerQueueReason, string> = {
  unknown_test_code:
    'The analyzer test code is not mapped to an analyte. Map it in Settings, Lab analyzers.',
  sample_not_found: 'No sample at this facility has this number. Check the barcode.',
  sample_not_usable: 'The sample was rejected or entered in error.',
  test_not_ordered: 'No request on this sample includes this test.',
  report_not_open:
    'The results for this request were already submitted or released. Return them for correction first.',
  differs_from_entered: 'A different value is already entered for this test.',
  unit_mismatch: 'The analyzer sent a different unit from the one mapped.',
  value_invalid: 'The value is not in the format this analyte takes.',
  value_not_possible: 'The value is outside the possible limits for this analyte.',
  status_not_final: 'The analyzer marked this result as not final.',
  ambiguous_request:
    'More than one request on this sample includes this test. Enter the result by hand.',
  ranges_conflict:
    'The ranges for this analyte overlap, so the value cannot be judged. Ask the clinical lead to correct them.',
  qc_not_set_up:
    'This is a QC control, but no target is set for this lot, analyte and analyzer. Set one in Settings, Lab quality control.',
};

export const ANALYZER_RESULT_STATES = ['applied', 'queued', 'dismissed'] as const;

export const analyzerQueueItemSchema = z.object({
  id: z.uuid(),
  analyzerId: z.uuid(),
  analyzerName: z.string(),
  receivedAt: z.iso.datetime(),
  observedAt: z.iso.datetime().nullable(),
  sampleNumber: z.string(),
  analyzerTestCode: z.string(),
  analyteName: z.string().nullable(),
  value: z.string(),
  unit: z.string().nullable(),
  analyzerFlags: z.string().nullable(),
  resultStatus: z.string().nullable(),
  state: z.enum(ANALYZER_RESULT_STATES),
  reason: z.enum(ANALYZER_QUEUE_REASONS).nullable(),
  reasonText: z.string().nullable(),
  /** The sample's ID when it was found, for opening result entry. */
  sampleId: z.uuid().nullable(),
  resolution: z
    .object({ by: actorSchema, at: z.iso.datetime(), note: z.string().nullable() })
    .nullable(),
});
export type AnalyzerQueueItemView = z.infer<typeof analyzerQueueItemSchema>;

export const analyzerQueueQuerySchema = z.object({
  state: z.enum(['queued', 'dismissed', 'applied']).default('queued'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.uuid().optional(),
});

export const applyAnalyzerResultBodySchema = z.object({
  /** A corrected sample number, when the barcode was misread. */
  sampleNumber: z.string().trim().min(1).max(64).optional(),
  /** Replace a different value already entered (kept in history as amended). */
  replaceEntered: z.boolean().default(false),
  /** Apply a result the analyzer marked as not final. */
  acceptNotFinal: z.boolean().default(false),
});

export const dismissAnalyzerResultBodySchema = z.object({ reason: reasonSchema });
